import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { AccountGroup, GUID } from '@ndvr/portfolio-api';

import { PortfolioApi } from 'API';
import { Toast } from 'widgets';

import { buildPolicyUpdate, PolicyDrafts } from './policy-update';

/** Saves every pending Investment Policy section draft in a single, atomic backend call. */
export const usePolicySave = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    const { mutateAsync } = useMutation({
        mutationFn: async (drafts: PolicyDrafts) => {
            // Fresh rather than cached, so configs added elsewhere are passed through instead of deleted.
            const [stored] = drafts.withdrawalPlan
                ? await PortfolioApi().listCashFlowConfigs(accountGroupGUID)
                : [undefined];
            await PortfolioApi().updateInvestmentPolicy(accountGroupGUID, buildPolicyUpdate(drafts, stored ?? []));
        },
        // Returned, so the save only resolves once the refetched data is in: the drafts are cleared after that and the
        // page never falls back to the stale saved data in between.
        onSuccess: async () => {
            // The allocation run is slow and only feeds the secured figures, so it refreshes in the background.
            queryClient.invalidateQueries({ queryKey: ['optimization', 'allocation'] });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID] }),
                queryClient.invalidateQueries({ queryKey: ['account'] }),
            ]);
            toast.success('Investment policy saved.');
        },
        onError: (error) => Toast.forError(error, 'Failed to save investment policy'),
    });

    return mutateAsync;
};
